import React, { useState, useEffect, Fragment } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import axios from 'axios';
import toast from 'react-hot-toast';
import { GlassCard, GlassButton, GlassBadge } from '../../components/ui/GlassComponents';
import { countWorkingDays } from '../../utils/leaveDayCount';
import { Dialog, Transition } from '@headlessui/react';
import { useConfirm } from '../../components/ui/ConfirmDialog';

const LeaveHistory = () => {
  const { user } = useAuth();
  const confirm = useConfirm();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [leaves, setLeaves] = useState([]);
  const [filteredLeaves, setFilteredLeaves] = useState([]);
  const [leaveTypes, setLeaveTypes] = useState([]);
  const isTeamAdmin = user && (user.role === 'team_admin' || user.role === 'super_admin');
  
  // State for leave details modal
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedLeave, setSelectedLeave] = useState(null);
  
  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const [workingDays, setWorkingDays] = useState(null); // null falls back to Mon-Fri in the util
  
  // Get parameters from URL query parameters
  const typeFromUrl = searchParams.get('type');
  const statusFromUrl = searchParams.get('status');
  
  // Filters
  const [filters, setFilters] = useState({
    status: statusFromUrl || 'all',
    type: typeFromUrl || 'all',
    year: new Date().getFullYear().toString(),
    searchQuery: ''
  });
  
  // Sorting
  const [sortConfig, setSortConfig] = useState({
    key: 'startDate',
    direction: 'desc'
  });
  
  useEffect(() => {
    // Fetch both leave history and leave types
    const fetchData = async () => {
      setLoading(true);
      try {
        // Fetch leave history
        await fetchLeaveHistory();
        
        // Fetch leave types
        const typesResponse = await axios.get('/api/leaves/types');
        setLeaveTypes(typesResponse.data || []);

        // Pick up team workingDays for accurate duration display
        try {
          const teamResponse = await axios.get('/api/teams/current');
          if (teamResponse.data && Array.isArray(teamResponse.data.workingDays)) {
            setWorkingDays(teamResponse.data.workingDays);
          }
        } catch (e) {
          // Soft failure — util defaults to Mon-Fri.
        }
      } catch (error) {
        console.error('Error fetching data:', error);
        toast.error('Failed to load leave data');
      } finally {
        setLoading(false);
      }
    };
    
    fetchData();
  }, []);
  
  useEffect(() => {
    // Apply filters and sorting
    let result = [...leaves];
    
    // Filter by status
    if (filters.status !== 'all') {
      result = result.filter(leave => leave.status === filters.status);
    }
    
    // Filter by type
    if (filters.type !== 'all') {
      result = result.filter(leave => leave.type === filters.type);
    }
    
    // Filter by year
    if (filters.year !== 'all') {
      const year = parseInt(filters.year);
      result = result.filter(leave => {
        const leaveYear = new Date(leave.startDate).getFullYear();
        return leaveYear === year;
      });
    }
    
    // Filter by search query
    if (filters.searchQuery) {
      const query = filters.searchQuery.toLowerCase();
      result = result.filter(leave => 
        leave.notes?.toLowerCase().includes(query) ||
        leave.type.toLowerCase().includes(query) ||
        leave.status.toLowerCase().includes(query)
      );
    }
    
    // Apply sorting
    if (sortConfig.key) {
      result.sort((a, b) => {
        if (a[sortConfig.key] < b[sortConfig.key]) {
          return sortConfig.direction === 'asc' ? -1 : 1;
        }
        if (a[sortConfig.key] > b[sortConfig.key]) {
          return sortConfig.direction === 'asc' ? 1 : -1;
        }
        return 0;
      });
    }
    
    setFilteredLeaves(result);
    setCurrentPage(1); // Reset to first page when filters change
  }, [leaves, filters, sortConfig]);
  
  const fetchLeaveHistory = async () => {
    try {
      const response = await axios.get('/api/leaves/history');
      setLeaves(response.data);
    } catch (error) {
      console.error('Error fetching leave history:', error);
      toast.error('Failed to load leave history');
    }
  };
  
  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({
      ...prev,
      [name]: value
    }));
  };
  
  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };
  
  const handleCancelRequest = async (id) => {
    const ok = await confirm({
      title: 'Cancel this leave request?',
      description: 'This will withdraw the request. You can submit a new one later.',
      confirmLabel: 'Cancel request',
      cancelLabel: 'Keep it',
      destructive: true
    });
    if (!ok) return;
    
    try {
      await axios.patch(`/api/leaves/${id}/cancel`);
      toast.success('Leave request cancelled successfully');
      
      // Update the local state
      setLeaves(prev => 
        prev.map(leave => 
          leave.id === id ? { ...leave, status: 'cancelled' } : leave
        )
      );
    } catch (error) {
      console.error('Error cancelling leave request:', error);
      toast.error('Failed to cancel leave request');
    }
  };
  
  const handleDeleteLeave = async (id, userName, leaveType) => {
    const leaveTypeName = leaveType || 'leave';
    const ok = await confirm({
      title: `Delete this ${leaveTypeName} request for ${userName}?`,
      description: 'Their available leave days will be restored.',
      confirmLabel: 'Delete',
      destructive: true
    });
    if (!ok) return;
    
    try {
      await axios.delete(`/api/leaves/${id}`);
      toast.success('Leave request deleted successfully');
      
      // Remove from local state
      setLeaves(prev => prev.filter(leave => leave.id !== id));
    } catch (error) {
      console.error('Error deleting leave request:', error);
      toast.error('Failed to delete leave request');
    }
  };
  
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };
  
  const calculateDuration = (startDate, endDate, halfDay) => {
    const isHalfDay = halfDay === true || halfDay === 'true';
    const finalCount = countWorkingDays({ startDate, endDate, workingDays, halfDay: isHalfDay });
    if (finalCount === 0) return '0 days';
    if (finalCount === 0.5) return '0.5 day';
    if (finalCount === 1) return '1 day';
    return `${finalCount} days`;
  };
  
  const handleViewLeave = (leave) => {
    setSelectedLeave(leave);
    setIsModalOpen(true);
  };
  
  const closeModal = () => {
    setIsModalOpen(false);
    setSelectedLeave(null);
  };
  
  const getStatusBadgeClass = (status) => {
    switch (status) {
      case 'approved':
        return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300';
      case 'pending':
        return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-300';
      case 'rejected':
        return 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300';
      case 'cancelled':
        return 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300';
      default:
        return 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300';
    }
  };
  
  const getLeaveTypeColor = (leave) => {
    // If we have a typeColor from the backend, use it to generate Tailwind classes
    if (leave && leave.typeColor && typeof leave === 'object') {
      // Extract color name from hex code or use the color name directly
      let colorName = 'blue';
      
      // Map common hex codes to Tailwind color names
      const colorMap = {
        '#4CAF50': 'green',
        '#E91E63': 'red',
        '#2563EB': 'blue',
        '#059669': 'green',
        '#7C3AED': 'purple',
        '#F59E0B': 'yellow',
        '#10B981': 'emerald',
        '#6366F1': 'indigo'
      };
      
      if (leave.typeColor in colorMap) {
        colorName = colorMap[leave.typeColor];
      }
      
      return `bg-${colorName}-100 text-${colorName}-800 dark:bg-${colorName}-900 dark:text-${colorName}-300`;
    }
    
    // If no typeColor is available, use a default color
    return 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300';
  };
  
  // Get current leaves for pagination
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentLeaves = filteredLeaves.slice(indexOfFirstItem, indexOfLastItem);
  
  // Change page
  const paginate = (pageNumber) => setCurrentPage(pageNumber);
  
  // Generate years for filter dropdown (last 5 years)
  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: 5 }, (_, i) => currentYear - i);
  
  // We'll continue with the render part in the next update
  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 dark:from-gray-900 dark:via-blue-900 dark:to-indigo-900">
      <div className="container mx-auto px-4 py-8 space-y-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
          <h1 className="text-2xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent animate-fade-in">Leave History</h1>
          <GlassButton className="mt-3 sm:mt-0">
            <Link to="/dashboard/leave-request" className="flex items-center gap-2">
              New Leave Request
            </Link>
          </GlassButton>
        </div>
        
        {/* Filters */}
        <GlassCard className="p-4 animate-fade-in">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <label htmlFor="status" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
              Status
            </label>
            <select
              id="status"
              name="status"
              value={filters.status}
              onChange={handleFilterChange}
              className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm rounded-md dark:bg-gray-700 dark:border-gray-600 dark:text-white"
            >
              <option value="all">All Statuses</option>
              <option value="pending">Pending</option>
              <option value="approved">Approved</option>
              <option value="rejected">Rejected</option>
              <option value="cancelled">Cancelled</option>
            </select>
          </div>
          
          <div>
            <label htmlFor="type" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
              Leave Type
            </label>
            <select
              id="type"
              name="type"
              value={filters.type}
              onChange={handleFilterChange}
              className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm rounded-md dark:bg-gray-700 dark:border-gray-600 dark:text-white"
            >
              <option value="all">All Types</option>
              {leaveTypes.map(type => (
                <option key={type.id} value={type.id}>
                  {type.name}
                </option>
              ))}
            </select>
          </div>
          
          <div>
            <label htmlFor="year" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
              Year
            </label>
            <select
              id="year"
              name="year"
              value={filters.year}
              onChange={handleFilterChange}
              className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm rounded-md dark:bg-gray-700 dark:border-gray-600 dark:text-white"
            >
              <option value="all">All Years</option>
              {years.map(year => (
                <option key={year} value={year}>{year}</option>
              ))}
            </select>
          </div>
          
          <div>
            <label htmlFor="searchQuery" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
              Search
            </label>
            <input
              type="text"
              name="searchQuery"
              id="searchQuery"
              value={filters.searchQuery}
              onChange={handleFilterChange}
              placeholder="Search notes, type, status..."
              className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 sm:text-sm dark:bg-gray-700 dark:border-gray-600 dark:text-white"
            />
          </div>
        </div>
        </GlassCard>
      
      {/* Leave History Table */}
      <div className="bg-white dark:bg-gray-800 shadow rounded-lg overflow-hidden">
        {loading ? (
          <div className="flex justify-center items-center h-64">
            <svg className="animate-spin h-10 w-10 text-primary-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
          </div>
        ) : filteredLeaves.length === 0 ? (
          <div className="text-center py-12">
            <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            <h3 className="mt-2 text-sm font-medium text-gray-900 dark:text-white">No leave requests found</h3>
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
              {filters.status !== 'all' || filters.type !== 'all' || filters.year !== 'all' || filters.searchQuery
                ? 'Try adjusting your filters to see more results.'
                : 'Get started by creating a new leave request.'}
            </p>
            {filters.status === 'all' && filters.type === 'all' && filters.year === 'all' && !filters.searchQuery && (
              <div className="mt-6">
                <Link to="/dashboard/leave-request" className="btn btn-primary">
                  New Leave Request
                </Link>
              </div>
            )}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead className="bg-gray-50 dark:bg-gray-700">
                <tr>
                  {isTeamAdmin && (
                    <th
                      scope="col"
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider cursor-pointer"
                      onClick={() => handleSort('userName')}
                    >
                      <div className="flex items-center">
                        Team Member
                        {sortConfig.key === 'userName' && (
                          <span className="ml-1">
                            {sortConfig.direction === 'asc' ? '↑' : '↓'}
                          </span>
                        )}
                      </div>
                    </th>
                  )}
                  <th
                    scope="col"
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider cursor-pointer"
                    onClick={() => handleSort('type')}
                  >
                    <div className="flex items-center">
                      Type
                      {sortConfig.key === 'type' && (
                        <span className="ml-1">
                          {sortConfig.direction === 'asc' ? '↑' : '↓'}
                        </span>
                      )}
                    </div>
                  </th>
                  <th
                    scope="col"
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider cursor-pointer"
                    onClick={() => handleSort('startDate')}
                  >
                    <div className="flex items-center">
                      Dates
                      {sortConfig.key === 'startDate' && (
                        <span className="ml-1">
                          {sortConfig.direction === 'asc' ? '↑' : '↓'}
                        </span>
                      )}
                    </div>
                  </th>
                  <th
                    scope="col"
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider"
                  >
                    Duration
                  </th>
                  <th
                    scope="col"
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider cursor-pointer"
                    onClick={() => handleSort('status')}
                  >
                    <div className="flex items-center">
                      Status
                      {sortConfig.key === 'status' && (
                        <span className="ml-1">
                          {sortConfig.direction === 'asc' ? '↑' : '↓'}
                        </span>
                      )}
                    </div>
                  </th>
                  <th
                    scope="col"
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider"
                  >
                    Notes
                  </th>
                  <th
                    scope="col"
                    className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider"
                  >
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                {currentLeaves.map((leave) => (
                  <tr key={leave.id} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                    {isTeamAdmin && (
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900 dark:text-white">
                          {leave.userName || (leave.User ? `${leave.User.firstName} ${leave.User.lastName}` : 'Unknown User')}
                        </div>
                      </td>
                    )}
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${getLeaveTypeColor(leave)}`}>
                        {leave.typeName || (leave.type ? (leave.type.charAt(0).toUpperCase() + leave.type.slice(1)) : 'Custom')}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900 dark:text-white">{formatDate(leave.startDate)}</div>
                      {leave.startDate !== leave.endDate && (
                        <div className="text-sm text-gray-500 dark:text-gray-400">to {formatDate(leave.endDate)}</div>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900 dark:text-white">{calculateDuration(leave.startDate, leave.endDate, leave.halfDay)}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusBadgeClass(leave.status)}`}>
                        {leave.status.charAt(0).toUpperCase() + leave.status.slice(1)}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm text-gray-900 dark:text-white truncate max-w-xs">{leave.notes || '-'}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex justify-end space-x-2">
                        {leave.status === 'pending' && leave.userId === user.id && (
                          <>
                            <Link to={`/dashboard/leave-request/${leave.id}`} className="text-primary-600 hover:text-primary-900 dark:text-primary-400 dark:hover:text-primary-300">
                              Edit
                            </Link>
                            <button
                              onClick={() => handleCancelRequest(leave.id)}
                              className="text-red-600 hover:text-red-900 dark:text-red-400 dark:hover:text-red-300"
                            >
                              Cancel
                            </button>
                          </>
                        )}
                        {leave.status !== 'pending' && (
                          <button 
                            onClick={() => handleViewLeave(leave)} 
                            className="text-primary-600 hover:text-primary-900 dark:text-primary-400 dark:hover:text-primary-300"
                          >
                            View
                          </button>
                        )}
                        {/* Delete button for team admins */}
                        {isTeamAdmin && leave.status === 'approved' && (
                          <button
                            onClick={() => handleDeleteLeave(leave.id, leave.userName || (leave.User ? `${leave.User.firstName} ${leave.User.lastName}` : 'user'), leave.typeName || (leave.type ? (leave.type.charAt(0).toUpperCase() + leave.type.slice(1)) : 'Custom'))}
                            className="text-red-600 hover:text-red-900 dark:text-red-400 dark:hover:text-red-300 flex items-center"
                            title="Delete leave and restore days"
                          >
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                            </svg>
                            Delete
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        
        {/* Pagination */}
        {!loading && filteredLeaves.length > 0 && (
          <div className="bg-white dark:bg-gray-800 px-4 py-3 flex items-center justify-between border-t border-gray-200 dark:border-gray-700 sm:px-6">
            <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
              <div>
                <p className="text-sm text-gray-700 dark:text-gray-300">
                  Showing <span className="font-medium">{indexOfFirstItem + 1}</span> to{' '}
                  <span className="font-medium">
                    {indexOfLastItem > filteredLeaves.length ? filteredLeaves.length : indexOfLastItem}
                  </span>{' '}
                  of <span className="font-medium">{filteredLeaves.length}</span> results
                </p>
              </div>
              <div>
                <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
                  <button
                    onClick={() => paginate(currentPage - 1)}
                    disabled={currentPage === 1}
                    className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white dark:bg-gray-700 dark:border-gray-600 text-sm font-medium text-gray-500 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <span className="sr-only">Previous</span>
                    <svg className="h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                      <path fillRule="evenodd" d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                  </button>
                  
                  {Array.from({ length: Math.ceil(filteredLeaves.length / itemsPerPage) }).map((_, index) => {
                    const pageNumber = index + 1;
                    // Show limited page numbers with ellipsis
                    if (
                      pageNumber === 1 ||
                      pageNumber === Math.ceil(filteredLeaves.length / itemsPerPage) ||
                      (pageNumber >= currentPage - 1 && pageNumber <= currentPage + 1)
                    ) {
                      return (
                        <button
                          key={pageNumber}
                          onClick={() => paginate(pageNumber)}
                          className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${currentPage === pageNumber
                            ? 'z-10 bg-primary-50 border-primary-500 text-primary-600 dark:bg-primary-900 dark:border-primary-500 dark:text-primary-300'
                            : 'bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-600'
                          }`}
                        >
                          {pageNumber}
                        </button>
                      );
                    } else if (
                      pageNumber === currentPage - 2 ||
                      pageNumber === currentPage + 2
                    ) {
                      return (
                        <span
                          key={pageNumber}
                          className="relative inline-flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-sm font-medium text-gray-700 dark:text-gray-300"
                        >
                          ...
                        </span>
                      );
                    }
                    return null;
                  })}
                  
                  <button
                    onClick={() => paginate(currentPage + 1)}
                    disabled={currentPage === Math.ceil(filteredLeaves.length / itemsPerPage)}
                    className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white dark:bg-gray-700 dark:border-gray-600 text-sm font-medium text-gray-500 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <span className="sr-only">Next</span>
                    <svg className="h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                      <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
                    </svg>
                  </button>
                </nav>
              </div>
            </div>
            
            <div className="flex sm:hidden">
              <button
                onClick={() => paginate(currentPage - 1)}
                disabled={currentPage === 1}
                className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Previous
              </button>
              <div className="mx-2 flex items-center">
                <span className="text-sm text-gray-700 dark:text-gray-300">
                  {currentPage} / {Math.ceil(filteredLeaves.length / itemsPerPage)}
                </span>
              </div>
              <button
                onClick={() => paginate(currentPage + 1)}
                disabled={currentPage === Math.ceil(filteredLeaves.length / itemsPerPage)}
                className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Leave Details Modal */}
      <Transition appear show={isModalOpen} as={Fragment}>
        <Dialog as="div" className="fixed inset-0 z-10 overflow-y-auto" onClose={closeModal}>
          <div className="min-h-screen px-4 text-center">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0"
              enterTo="opacity-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100"
              leaveTo="opacity-0"
            >
              <Dialog.Overlay className="fixed inset-0 bg-black opacity-30" />
            </Transition.Child>

            {/* This element is to trick the browser into centering the modal contents. */}
            <span className="inline-block h-screen align-middle" aria-hidden="true">
              &#8203;
            </span>
            
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <div className="inline-block w-full max-w-md p-6 my-8 overflow-hidden text-left align-middle transition-all transform bg-white dark:bg-gray-800 shadow-xl rounded-2xl">
                <Dialog.Title as="h3" className="text-lg font-medium leading-6 text-gray-900 dark:text-white">
                  Leave Details
                </Dialog.Title>
                
                {selectedLeave && (
                  <div className="mt-4 space-y-4">
                    {/* Leave Type */}
                    <div>
                      <h4 className="text-sm font-medium text-gray-500 dark:text-gray-400">Type</h4>
                      <p className="mt-1">
                        <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${getLeaveTypeColor(selectedLeave)}`}>
                          {selectedLeave.typeName || (selectedLeave.type ? (selectedLeave.type.charAt(0).toUpperCase() + selectedLeave.type.slice(1)) : 'Custom')}
                        </span>
                      </p>
                    </div>
                    
                    {/* Status */}
                    <div>
                      <h4 className="text-sm font-medium text-gray-500 dark:text-gray-400">Status</h4>
                      <p className="mt-1">
                        <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusBadgeClass(selectedLeave.status)}`}>
                          {selectedLeave.status.charAt(0).toUpperCase() + selectedLeave.status.slice(1)}
                        </span>
                      </p>
                    </div>
                    
                    {/* Dates */}
                    <div>
                      <h4 className="text-sm font-medium text-gray-500 dark:text-gray-400">Dates</h4>
                      <p className="mt-1 text-sm text-gray-900 dark:text-white">
                        {formatDate(selectedLeave.startDate)}
                        {selectedLeave.startDate !== selectedLeave.endDate && (
                          <span> to {formatDate(selectedLeave.endDate)}</span>
                        )}
                      </p>
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                        {calculateDuration(selectedLeave.startDate, selectedLeave.endDate, selectedLeave.halfDay)}
                      </p>
                    </div>
                    
                    {/* Half Day */}
                    {selectedLeave.halfDay && (
                      <div>
                        <h4 className="text-sm font-medium text-gray-500 dark:text-gray-400">Half Day</h4>
                        <p className="mt-1 text-sm text-gray-900 dark:text-white">Yes</p>
                      </div>
                    )}
                    
                    {/* Notes */}
                    <div>
                      <h4 className="text-sm font-medium text-gray-500 dark:text-gray-400">Notes</h4>
                      <p className="mt-1 text-sm text-gray-900 dark:text-white whitespace-pre-wrap">
                        {selectedLeave.notes || 'No notes provided'}
                      </p>
                    </div>
                    
                    {/* Request Date */}
                    <div>
                      <h4 className="text-sm font-medium text-gray-500 dark:text-gray-400">Request Date</h4>
                      <p className="mt-1 text-sm text-gray-900 dark:text-white">
                        {formatDate(selectedLeave.createdAt)}
                      </p>
                    </div>
                    
                    {/* Approval/Rejection Date if applicable */}
                    {selectedLeave.updatedAt && selectedLeave.updatedAt !== selectedLeave.createdAt && (
                      <div>
                        <h4 className="text-sm font-medium text-gray-500 dark:text-gray-400">
                          {selectedLeave.status === 'approved' ? 'Approval Date' : 
                           selectedLeave.status === 'rejected' ? 'Rejection Date' : 
                           selectedLeave.status === 'cancelled' ? 'Cancellation Date' : 'Update Date'}
                        </h4>
                        <p className="mt-1 text-sm text-gray-900 dark:text-white">
                          {formatDate(selectedLeave.updatedAt)}
                        </p>
                      </div>
                    )}
                    
                    {/* Approver information if available */}
                    {selectedLeave.status === 'approved' && selectedLeave.approvedBy && (
                      <div>
                        <h4 className="text-sm font-medium text-gray-500 dark:text-gray-400">Approved By</h4>
                        <p className="mt-1 text-sm text-gray-900 dark:text-white">
                          {selectedLeave.approvedBy}
                        </p>
                      </div>
                    )}
                  </div>
                )}

                <div className="mt-6">
                  <button
                    type="button"
                    className="inline-flex justify-center px-4 py-2 text-sm font-medium text-white bg-primary-600 border border-transparent rounded-md hover:bg-primary-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-primary-500"
                    onClick={closeModal}
                  >
                    Close
                  </button>
                </div>
              </div>
            </Transition.Child>
          </div>
        </Dialog>
      </Transition>
      </div>
    </div>
  );
};

export default LeaveHistory;
